import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Navigation, Send } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { COMPANY_LOGO, COMPANY_NAME, PRODUCT_NAME } from "../lib/brand";

const ROLE_OPTIONS = [
  {
    role: "shipper",
    title: "Shipper",
    description: "Post loads, request quotes, track shipments, and manage documents.",
    icon: Send,
  },
  {
    role: "carrier_owner",
    title: "Carrier / Broker",
    description: "Browse the load board, place bids, and coordinate active shipments.",
    icon: Building2,
  },
  {
    role: "driver",
    title: "Driver",
    description: "Find nearby loads, manage matches, and share live location updates.",
    icon: Navigation,
  },
];

export default function RoleOnboarding() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [error, setError] = useState("");

  const selectRole = async (role) => {
    setSelectedRole(role);
    setError("");
    try {
      await api.post("/auth/select-role", { role });
      await refresh();
      navigate("/app", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to save your role. Please try again.");
      setSelectedRole(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-navy text-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-white flex items-center justify-center overflow-hidden">
            <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display text-lg font-bold leading-tight">{COMPANY_NAME}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold">on {PRODUCT_NAME}</div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Account setup</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">
              Choose your workspace
            </h1>
            <p className="text-slate-500 mt-3 max-w-2xl">
              {user?.name ? `${user.name}, select how you plan to use SpeedyShip.` : "Select how you plan to use SpeedyShip."}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {ROLE_OPTIONS.map(({ role, title, description, icon: Icon }) => {
              const busy = selectedRole === role;
              return (
                <button
                  key={role}
                  type="button"
                  disabled={Boolean(selectedRole)}
                  onClick={() => selectRole(role)}
                  className="card-base p-5 text-left hover:border-gold hover:shadow-md transition-all disabled:opacity-60"
                  data-testid={`select-role-${role}`}
                >
                  <span className="w-11 h-11 rounded-md bg-navy/5 text-navy flex items-center justify-center mb-5">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <span className="block font-display text-xl font-bold text-navy mb-2">{title}</span>
                  <span className="block text-sm leading-6 text-slate-500">{description}</span>
                  <span className="btn-primary mt-6 w-full justify-center py-2.5">
                    {busy ? "Saving..." : "Continue"}
                  </span>
                </button>
              );
            })}
          </div>

          {error && (
            <div className="mt-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="role-error">
              {error}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
